import React, { useState, useEffect, useRef } from "react";
import { motion } from "motion/react";
import { useSelector } from "react-redux";
import { getAccountType } from "../../store/auth/selectors";
import {
  checkPermission,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";
import StudentTable from "../../shared/components/Table/StudentTable";
import Pagination from "../../shared/components/Pagination/Pagination";
import AnimatedCount from "../../shared/components/AnimatedCount/AnimatedCount";
import downloading from "../../assets/gifs/downloading.gif";
import Tooltip from "@mui/material/Tooltip";
import loader from "../../assets/gifs/loader.gif";

const StudentsTabContent = ({
  error,
  isLoading,
  paginatedStudents,
  totalStudents,
  operation,
  selectedStudents,
  currentPage,
  itemsPerPage,
  onOperationChange,
  onPageChange,
  onItemsPerPageChange,
  onSelectAll,
  onSelectRow,
  columns,
  onExportCSV,
  isExporting,
  canExport,
}) => {
  const accountType = useSelector(getAccountType);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const canResetPassword = checkUserPermission(
    PERMISSION_TYPES.RESET_STUDENT_PASSWORD
  );
  const canDeleteStudent = checkUserPermission(PERMISSION_TYPES.DELETE_STUDENT);

  const canExportStudents =
    canExport && checkUserPermission(PERMISSION_TYPES.EXPORT_STUDENT_DATA);
  const isOperationDisabled = selectedStudents.length === 0;

  const [displayTotal, setDisplayTotal] = useState(0);
  const countTimerRef = useRef(null);
  const previousLoadingRef = useRef(isLoading);

  // Handle delay for animation when loading completes
  useEffect(() => {
    if (isLoading) {
      // Reset to 0 when loading starts
      setDisplayTotal(0);
      previousLoadingRef.current = true;
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
        countTimerRef.current = null;
      }
      return;
    }

    // When loading completes, ensure we start at 0, then animate to target
    if (previousLoadingRef.current && !isLoading) {
      // Just finished loading: reset to 0 first (component will mount with 0)
      setDisplayTotal(0);

      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }

      // Use requestAnimationFrame to ensure component is mounted and painted
      // then setTimeout to give the spring time to initialize before animating
      const rafId = requestAnimationFrame(() => {
        countTimerRef.current = setTimeout(() => {
          setDisplayTotal(totalStudents || 0);
          countTimerRef.current = null;
        }, 150);
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (countTimerRef.current) {
          clearTimeout(countTimerRef.current);
        }
      };
    } else if (
      !previousLoadingRef.current &&
      !isLoading &&
      totalStudents !== undefined
    ) {
      // Already loaded, but totalStudents changed: animate to new value
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }

      countTimerRef.current = setTimeout(() => {
        setDisplayTotal(totalStudents || 0);
        countTimerRef.current = null;
      }, 150);
    }

    previousLoadingRef.current = isLoading;

    return () => {
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }
    };
  }, [isLoading, totalStudents]);

  // Defensive check to prevent race condition bug:
  // - If totalStudents > 0, we know there's data (even if paginatedStudents is temporarily empty due to React state update timing)
  // - Only show "no data" if we're certain: !isLoading && totalStudents === 0 && no error
  const hasData =
    Array.isArray(paginatedStudents) && paginatedStudents.length > 0;
  const hasTotalStudents = totalStudents > 0;
  // Show table if we have data OR if totalStudents indicates data exists (defensive against race conditions)
  const conditionCheck = !error && !isLoading && (hasData || hasTotalStudents);
  return (
    <>
      {error ? (
        <div className="error-message">{error}</div>
      ) : isLoading ? (
        <div className="loading-container">
          <img
            src={loader}
            alt="Loading"
            style={{ width: "200px", height: "200px", margin: "auto" }}
          />
        </div>
      ) : conditionCheck ? (
        <>
          <div className="students-info">
            <span style={{ fontWeight: "500" }}>
              Total Students: <AnimatedCount value={displayTotal} />
            </span>

            <motion.div
              className="table-controls"
              layout
              transition={{
                layout: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] },
              }}
            >
              {canExportStudents && (
                <div className="export-container">
                  {isExporting ? (
                    <div className="export-loading">
                      <img
                        src={downloading}
                        alt="Loading"
                        className="export-loader"
                      />
                      <span>Exporting...</span>
                    </div>
                  ) : (
                    <button
                      className="operation-select export-operations" // Re-using class for styling, can be more specific
                      onClick={onExportCSV}
                      disabled={isExporting || !paginatedStudents.length}
                      title={
                        !paginatedStudents.length
                          ? "No data to export"
                          : "Export student data as CSV"
                      }
                    >
                      Export CSV
                    </button>
                  )}
                </div>
              )}
              {isOperationDisabled ? (
                <Tooltip title="Please select students first" followCursor>
                  <div>
                    <select
                      className="operation-select bulk-operations"
                      value={operation}
                      onChange={onOperationChange}
                      disabled={true}
                    >
                      <option value="">Select Operation</option>
                      {canDeleteStudent && (
                        <option value="delete">Delete Student</option>
                      )}
                      {canResetPassword && (
                        <option value="reset">Reset Password</option>
                      )}
                    </select>
                  </div>
                </Tooltip>
              ) : (
                <select
                  className="operation-select bulk-operations"
                  value={operation}
                  onChange={onOperationChange}
                  disabled={false}
                >
                  <option value="">Select Operation</option>
                  {canDeleteStudent && (
                    <option value="delete">Delete Student</option>
                  )}
                  {canResetPassword && (
                    <option value="reset">Reset Password</option>
                  )}
                </select>
              )}
              <Pagination
                totalItems={totalStudents}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                itemsPerPageOptions={[100, 200, 500, 1000, 2000]}
                onPageChange={onPageChange}
                onItemsPerPageChange={onItemsPerPageChange}
              />
            </motion.div>
          </div>

          <StudentTable
            data={paginatedStudents}
            columns={columns}
            selectedRows={selectedStudents}
            onSelectAll={onSelectAll}
            onSelectRow={onSelectRow}
            showRowSelector={canDeleteStudent || canResetPassword}
            isAllSelected={
              totalStudents > 0 && selectedStudents.length === totalStudents
            }
          />
        </>
      ) : (
        <div className="no-data-message">Please enter a school ID</div>
      )}
    </>
  );
};

export default StudentsTabContent;
